import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import toast from 'react-hot-toast';
import { Button } from "../../../components/Button/Button";
import ExpertSelectionList from "../components/ExpertSelectionList";
import ExpertSelectionCard from "../components/ExpertSelectionCard";
import { PostSuccessDialog } from "../components/PostSuccessDialog";
import { IconCircledCheck } from "../../../utils/icons";
import axiosInstant from "../../../api";
import { getBrandProject, getProjectPros } from "../../../api/endpoints";
import { DotLottleLoader } from "../../../components/Loader";
import { BRAND_PROJECT_STATUSES, sortExpertsBySelection, describeSharedSiblings, isProOptedIn, optInApprovalNote } from "../helper";
import { useProcessSiblings } from "../../../hooks/useProcessSiblings";
import { BriefDenialDialog } from "../../../components/Brief/BriefDenialDialog";

interface ExpertReviewModalProps {
  open: boolean;
  onClose: () => void;
  projectId: number;
  onConfirm: () => void;
}

const ExpertReviewModal = ({ open, onClose, projectId, onConfirm }: ExpertReviewModalProps) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [showDenialDialog, setShowDenialDialog] = useState(false);
  const [projectPros, setProjectPros] = useState<any[]>([]);
  const [project, setProject] = useState<any>(null);
  const [brandId, setBrandId] = useState<number | null>(null);
  // The reviewed roster is shared across the selection process; approve/deny
  // fans out to the sibling projects (internal AM modal).
  const sharedProcess = useProcessSiblings(
    project?.order_id != null ? Number(project.order_id) : undefined,
    project?.id,
    { siblingCount: project?.process_project_count }
  );
  const otherProcessProjects = sharedProcess.siblingProjects.filter(
    (p: any) => Number(p.id) !== Number(project?.id)
  );
  const [isLoading, setIsLoading] = useState(false);

  const fetchProjectPros = async () => {
    if (project?.id && brandId) {
      const url = getProjectPros(brandId, project.id);
      try {
        const res = await axiosInstant.get(url);
        const data = res.data.rows || [];
        const sorted = sortExpertsBySelection(data);
        setProjectPros(sorted);
        
      } catch (err) {
        console.error("Failed to fetch project pros:", err);
        setProjectPros([]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (open && project && brandId) {
      setIsLoading(true);
      fetchProjectPros();
    }
  }, [open, project, brandId]);

  useEffect(() => {
    if (projectId) {
      setIsLoading(true);
      axiosInstant.get(getBrandProject(Number(projectId))).then((response) => {
        setProject(response.data);
        setBrandId(response.data?.brands?.find((b: any) => b.rmn !== true)?.id);
      });
    }
  }, [projectId]);

  const handleStatusUpdate = async (status: any, reason?: string) => {
    if (!brandId || !project?.id) {
      toast.error('Project data missing');
      return;
    }

    try {

      // Shared roster — the API fans approve/deny out to the sibling projects
      // on its own (source notifies, siblings silent).
      await axiosInstant.patch(`/brands/${brandId}/projects/${project.id}`, {
        status,
        reason,
      });

      const message =
        status === BRAND_PROJECT_STATUSES.EXPERTS_SELECTION_NEEDED
          ? 'Successfully approved experts'
          : `Successfully denied experts`;

      toast.success(message);
      onConfirm();
      setShowSuccess(true);
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update project status. Please try again.');
    }
  };

  const handleApprove = () => {
    handleStatusUpdate(BRAND_PROJECT_STATUSES.EXPERTS_SELECTION_NEEDED);
  };

  const handleDeny = () => {
    setShowDenialDialog(true);
  };

  const sortedPros = sortExpertsBySelection(projectPros);
  const optedInCount = projectPros.filter(isProOptedIn).length;

  const topExpert = sortedPros[0];
  const otherExperts = sortedPros.slice(1);

  return (
    <>
      <Dialog open={open} maxWidth="lg" fullWidth onClose={onClose}>
        <DialogTitle className="border-b border-border-token pb-4">
          <div className="flex justify-between items-center">
            <Typography variant="h5">
              Expert Review
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon className="text-text-2" />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent className="!py-4">
          <div className="text-text-2 font-circular text-[14px] font-[450] leading-normal">
            Review the proposed experts before they are shared with the client.
          </div>
          {!isLoading && projectPros.length > 0 && (
            <div className="mt-2 text-accent-text font-circular text-[13px] font-[450]">
              {optInApprovalNote(optedInCount, projectPros.length)}
            </div>
          )}
          {sharedProcess.isShared && (
            <div className="mt-2 text-accent-text font-circular text-[13px] font-[450]">
              This roster is shared with {describeSharedSiblings(otherProcessProjects)};
              approving or denying applies to them too.
            </div>
          )}
          {isLoading ? (
            <div className="p-6 flex items-center justify-center h-[400px]">
              <DotLottleLoader fill={false} sx={{ width: 200, height: 200 }} />
            </div>
          ) : projectPros.length ? (
            <>
              {topExpert && (
                <>
                  <div className="text-accent-text font-circular text-[14px] font-[450] mt-[24px] mb-[16px]">
                    Top Recommendation!
                  </div>
                  <ExpertSelectionList
                    recommendationCheck={true}
                    pros={topExpert}
                    project={project}
                    onUpdate={fetchProjectPros}
                    selectedExperts={[]}
                    setSelectedExperts={undefined}
                    isApprovalPage={true}
                    sharedSiblingProjects={otherProcessProjects}
                  />
                </>
              )}

              {otherExperts.length > 0 && (
                <>
                  <div className="text-text-2 font-circular text-[14px] font-[450] my-[16px]">
                    Other Recommended Experts
                  </div>

                  <Box sx={{ overflowX: "auto", width: "100%", padding: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "center", sm: "stretch" },
                        gap: "16px",
                        minWidth: { xs: 0, sm: "fit-content" },
                        '& > *': {
                          width: { xs: "100%", sm: 300 },
                          flexShrink: 0,
                          height: "auto",
                        },
                      }}
                    >
                      {otherExperts.map((pro) => (
                        <ExpertSelectionCard
                          key={pro.userid}
                          pro={pro}
                          isSelected={false}
                          recommendationCheck={true}
                          project={project}
                          onUpdate={fetchProjectPros}
                          onScoreChange={undefined}
                          selectedExperts={[]}
                          setSelectedExperts={undefined}
                          approvalCheck={true}
                          isApprovalPage={true}
                          sharedSiblingProjects={otherProcessProjects}
                        />
                      ))}
                    </Box>
                  </Box>
                </>
              )}

            </>
          ) : (
            <div className="p-6 flex items-center justify-center h-[400px]">
              <div className="text-text-2 font-circular text-[14px] font-[450] leading-normal">
                No experts found
              </div>
            </div>
          )}
        </DialogContent>

        <DialogActions className="max-sm:!px-4 !px-6 !py-4 border-t border-border-token">
          <div className="flex max-sm:flex-col max-sm:items-start max-sm:gap-3 justify-between items-center w-full">
            <span className="text-text-2 text-[14px] font-circular">
              For Expert related questions, please contact our{" "}
              <a
                href="mailto:experts@thedesirecompany.com"
                target="_blank"
                rel="noreferrer"
                className="text-accent-text hover:underline cursor-pointer"
              >
                support team
              </a>
            </span>
            <div className="flex gap-4">
              <Button
                variant="contained"
                onClick={handleDeny}
                className="h-[40px] flex !py-0 !px-4 !min-w-fit !bg-[#EB3E33] hover:!bg-[#d82d21]"
              >
                Deny
              </Button>
              <Button
                variant="contained"
                onClick={handleApprove}
                className="h-[40px] flex !py-0 !px-4 !min-w-fit !bg-[#1FCE7A] hover:!bg-[#45ae7b]"
              >
                Approve
              </Button>
            </div>
          </div>
        </DialogActions>
      </Dialog>

      {showDenialDialog && (
        <BriefDenialDialog
          onCancel={() => setShowDenialDialog(false)}
          onOkay={(text) => {
            handleStatusUpdate(
              BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_DENIED,
              text
            );
            setShowDenialDialog(false);
          }}
        />
      )}

      {showSuccess && (
        <PostSuccessDialog
          icon={<IconCircledCheck />}
          title="Expert Review Complete"
          text="The expert review has been completed successfully."
          onClose={() => {
            setShowSuccess(false);
            onClose();
          }}
        />
      )}
    </>
  );
};

export default ExpertReviewModal;
